import type { ChannelMember } from '@src/api/shared/community/channelMemberApiClient';
import { brandColors } from '@src/assets/colors/brandColors';
import allIcon from '@src/assets/images/avatar/all.png';
import defaultUserIcon from '@src/assets/images/avatar/default.png';
import { ChannelMemberUserTypeEnum } from '@src/enum/ChannelMemberUserTypeEnum';
import { isAndroidDevice, isMobileDevice } from '@src/hooks/useIsMobile';
import { SHARED_URL } from '@src/routes/shared';
import { Plugin, PluginKey, TextSelection } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import { generatePath } from 'react-router-dom';

const MINIMUM_TOP_SPACING = 10; // Minimum distance from top of viewport when positioning popup (px)
const NON_BREAKING_SPACE = '\u00A0'; // HTML &nbsp; character
const MENTION_TEXT_PREFIX = '@';
const FULL_WIDTH_AT_SYMBOL = '＠'; // Full-width @ symbol

type PopupState = {
  container: HTMLDivElement | null;
  isOpen: boolean;
  isHidden: boolean;
  highlight: number;
  list: ChannelMember[];
  anchor: { left: number; top: number } | null;
  removeGlobalListeners?: () => void;
  wasClosedByOutsideClick?: boolean;
  lastQuery?: string;
};

const normalize = (text: string): string => {
  return text
    .replace(/[\s\u3000]+/g, ' ')
    .toLowerCase()
    .trim();
};

const ALL_ID = -1;
export const MentionPluginKey = new PluginKey<PopupState>('mention');

const isMobile = (): boolean => {
  return isMobileDevice();
};

const isAndroid = (): boolean => {
  return isAndroidDevice();
};

function calculatePopupHeight(memberCount: number, isMobileDevice: boolean): number {
  const baseRowHeight = 40;
  const padding = 8;
  const border = 2;
  const visibleCount = Math.min(memberCount, 10);

  const calculatedHeight = visibleCount * baseRowHeight + padding + border;
  const maxHeight = isMobileDevice ? 200 : 160;

  return Math.min(calculatedHeight, maxHeight);
}

const MENTION_SCROLL_START = 'mentionScrollStart';
const MENTION_SCROLL_END = 'mentionScrollEnd';
let isMentionCurrentlyScrolling = false;

function setupAndroidScrollFix(popupBox: HTMLDivElement): () => void {
  if (!isAndroid()) {
    return () => {};
  }

  let startY = 0;
  let startX = 0;
  let startScrollTop = 0;
  let isScrolling = false;
  let hasMoved = false;
  let touchStartTime = 0;

  const SCROLL_THRESHOLD = 10;
  const TAP_TIME_THRESHOLD = 200;

  const handleTouchStart = (e: TouchEvent) => {
    if (!e.touches.length) {
      return;
    }

    const touch = e.touches[0];
    const rect = popupBox.getBoundingClientRect();
    const isInsidePopup =
      touch.clientX >= rect.left &&
      touch.clientX <= rect.right &&
      touch.clientY >= rect.top &&
      touch.clientY <= rect.bottom;

    if (!isInsidePopup) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    startY = touch.clientY;
    startX = touch.clientX;
    startScrollTop = popupBox.scrollTop;
    isScrolling = false;
    hasMoved = false;
    touchStartTime = Date.now();

    isMentionCurrentlyScrolling = true;
    document.dispatchEvent(new CustomEvent(MENTION_SCROLL_START));
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!e.touches.length || !isMentionCurrentlyScrolling) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    const currentY = e.touches[0].clientY;
    const currentX = e.touches[0].clientX;
    const deltaY = Math.abs(startY - currentY);
    const deltaX = Math.abs(startX - currentX);

    if (deltaY > SCROLL_THRESHOLD || deltaX > SCROLL_THRESHOLD) {
      hasMoved = true;

      if (deltaY > deltaX) {
        const scrollDelta = startY - currentY;
        const newScrollTop = startScrollTop + scrollDelta;

        const canScrollUp = popupBox.scrollTop > 0;
        const canScrollDown = popupBox.scrollTop < popupBox.scrollHeight - popupBox.clientHeight;

        if ((scrollDelta > 0 && canScrollDown) || (scrollDelta < 0 && canScrollUp)) {
          isScrolling = true;
          popupBox.scrollTop = Math.max(
            0,
            Math.min(newScrollTop, popupBox.scrollHeight - popupBox.clientHeight),
          );
        }
      }
    }
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (!isMentionCurrentlyScrolling) {
      return;
    }

    const touchEndTime = Date.now();
    const touchDuration = touchEndTime - touchStartTime;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    if (isScrolling || (hasMoved && touchDuration > TAP_TIME_THRESHOLD)) {
      setTimeout(() => {
        isMentionCurrentlyScrolling = false;
        document.dispatchEvent(new CustomEvent(MENTION_SCROLL_END));
      }, 150);
    } else {
      isMentionCurrentlyScrolling = false;
      document.dispatchEvent(new CustomEvent(MENTION_SCROLL_END));
    }

    isScrolling = false;
    hasMoved = false;
  };

  popupBox.addEventListener('touchstart', handleTouchStart, { passive: false });
  popupBox.addEventListener('touchmove', handleTouchMove, { passive: false });
  popupBox.addEventListener('touchend', handleTouchEnd, { passive: false });

  return () => {
    popupBox.removeEventListener('touchstart', handleTouchStart);
    popupBox.removeEventListener('touchmove', handleTouchMove);
    popupBox.removeEventListener('touchend', handleTouchEnd);
    isMentionCurrentlyScrolling = false;

    document.dispatchEvent(new CustomEvent(MENTION_SCROLL_END));
  };
}

function setupScrollIsolation(popupBox: HTMLDivElement): () => void {
  let isScrolling = false;
  let lastTouchY = 0;

  const handleWheel = (e: WheelEvent) => {
    e.stopPropagation();

    const { scrollTop, scrollHeight, clientHeight } = popupBox;
    const atTop = scrollTop === 0;
    const atBottom = scrollTop + clientHeight >= scrollHeight;

    if ((atTop && e.deltaY < 0) || (atBottom && e.deltaY > 0)) {
      e.preventDefault();
    }
  };

  const handleTouchStart = (e: TouchEvent) => {
    isScrolling = false;
    lastTouchY = e.touches[0].clientY;
    e.stopPropagation();
  };

  const handleTouchMove = (e: TouchEvent) => {
    e.stopPropagation();

    const { scrollTop, scrollHeight, clientHeight } = popupBox;
    const atTop = scrollTop === 0;
    const atBottom = scrollTop + clientHeight >= scrollHeight;

    if (atTop || atBottom) {
      const touch = e.touches[0];
      const deltaY = touch.clientY - lastTouchY;

      if ((atTop && deltaY > 0) || (atBottom && deltaY < 0)) {
        e.preventDefault();
      }
    }

    lastTouchY = e.touches[0].clientY;
    isScrolling = true;
  };

  const handleTouchEnd = (e: TouchEvent) => {
    e.stopPropagation();
    if (isScrolling) {
      e.preventDefault();
    }
  };

  const handleScroll = (e: Event) => {
    e.stopPropagation();
  };

  popupBox.addEventListener('wheel', handleWheel, { passive: false });
  popupBox.addEventListener('touchstart', handleTouchStart, { passive: false });
  popupBox.addEventListener('touchmove', handleTouchMove, { passive: false });
  popupBox.addEventListener('touchend', handleTouchEnd, { passive: false });
  popupBox.addEventListener('scroll', handleScroll, { passive: false });

  return () => {
    popupBox.removeEventListener('wheel', handleWheel);
    popupBox.removeEventListener('touchstart', handleTouchStart);
    popupBox.removeEventListener('touchmove', handleTouchMove);
    popupBox.removeEventListener('touchend', handleTouchEnd);
    popupBox.removeEventListener('scroll', handleScroll);
  };
}

function makeRow(member: ChannelMember, view: EditorView, index: number): HTMLDivElement {
  const rowElement = document.createElement('div');
  Object.assign(rowElement.style, {
    display: 'flex',
    alignItems: 'center',
    padding: '4px 8px',
    cursor: 'pointer',
    fontSize: '14px',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
    transition: 'background-color 0.2s, color 0.2s',
  });

  rowElement.addEventListener('mouseover', () => {
    const currentState = MentionPluginKey.getState(view.state);
    if (!currentState || !currentState.container || currentState.highlight === index) {
      return;
    }

    const nextState = { ...currentState, highlight: index };
    view.dispatch(view.state.tr.setMeta(MentionPluginKey, { next: nextState }));

    paint(view, currentState.container, nextState.list, index);
  });

  if (member.id === ALL_ID) {
    const avatarImage = document.createElement('img');
    avatarImage.src = allIcon;
    Object.assign(avatarImage.style, {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      marginRight: '8px',
      userDrag: 'none',
      WebkitUserDrag: 'none',
    });
    rowElement.appendChild(avatarImage);
    const labelSpan = document.createElement('span');
    labelSpan.textContent = 'ALL';
    rowElement.appendChild(labelSpan);
    const descSpan = document.createElement('span');
    descSpan.textContent = '　すべてのメンバーに通知が送られます';
    Object.assign(descSpan.style, {
      fontFamily: 'Noto Sans JP',
      fontWeight: '400',
      fontSize: '12px',
      lineHeight: '150%',
      letterSpacing: '0%',
      WebkitBackgroundClip: 'text',
      color: '#292929',
    });
    rowElement.appendChild(descSpan);
  } else {
    const avatarImage = document.createElement('img');
    avatarImage.src = member.avatar_url?.trim() || defaultUserIcon;
    Object.assign(avatarImage.style, {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      marginRight: '8px',
      userDrag: 'none',
      WebkitUserDrag: 'none',
    });
    rowElement.appendChild(avatarImage);
    const labelSpan = document.createElement('span');
    labelSpan.textContent = member.display_name;
    rowElement.appendChild(labelSpan);
  }

  let touchStartTime = 0;
  let touchStartY = 0;
  let hasMovedDuringTouch = false;

  const handleTouchStart = (e: TouchEvent) => {
    touchStartTime = Date.now();
    touchStartY = e.touches[0]?.clientY || 0;
    hasMovedDuringTouch = false;
  };

  const handleTouchMove = (e: TouchEvent) => {
    const currentY = e.touches[0]?.clientY || 0;
    const deltaY = Math.abs(touchStartY - currentY);

    if (deltaY > 5) {
      hasMovedDuringTouch = true;
    }
  };

  const handleTouchEnd = (e: TouchEvent) => {
    const touchDuration = Date.now() - touchStartTime;

    if (!hasMovedDuringTouch && touchDuration < 300) {
      e.preventDefault();
      e.stopPropagation();

      setTimeout(() => {
        insertMention(view, member);
        closePopup(view);
      }, 50);
    }
  };

  const handleMouseDown = (e: MouseEvent) => {
    if (!isMobile()) {
      e.preventDefault();
      insertMention(view, member);
      closePopup(view);
    }
  };

  if (isMobile()) {
    rowElement.addEventListener('touchstart', handleTouchStart, { passive: false });
    rowElement.addEventListener('touchmove', handleTouchMove, { passive: false });
    rowElement.addEventListener('touchend', handleTouchEnd, { passive: false });
  } else {
    rowElement.addEventListener('mousedown', handleMouseDown);
  }

  return rowElement;
}

function paint(
  view: EditorView,
  popupBox: HTMLDivElement,
  fullList: ChannelMember[],
  highlightIndex: number,
): void {
  const visibleMembers = fullList.slice(0, 10);
  const clampedIndex = Math.min(highlightIndex, visibleMembers.length - 1);
  popupBox.innerHTML = '';
  visibleMembers.forEach((member, idx) => {
    const row = makeRow(member, view, idx);
    if (idx === clampedIndex) {
      row.style.background = brandColors['500'];
      row.style.color = 'white';
      const spans = row.getElementsByTagName('span');
      for (let i = 0; i < spans.length; i++) {
        spans[i].style.color = 'white';
      }
    }
    popupBox.appendChild(row);
  });
  (popupBox.children[clampedIndex] as HTMLDivElement | undefined)?.scrollIntoView({
    block: 'nearest',
  });
}

function addGlobalListeners(editorView: EditorView, popupBox: HTMLDivElement): () => void {
  let isClosing = false;

  const outsideHandler = (event: Event): void => {
    if (isClosing) {
      return;
    }

    const targetNode = event.target as Node | null;
    if (!targetNode || popupBox.contains(targetNode)) {
      return;
    }

    if (event.type === 'touchend' && isMentionCurrentlyScrolling) {
      setTimeout(() => {
        if (!isMentionCurrentlyScrolling) {
          triggerClose();
        }
      }, 100);
      return;
    }

    if (event.type === 'click' && !isMentionCurrentlyScrolling) {
      triggerClose();
      return;
    }

    if (event.type === 'touchend' && !isMentionCurrentlyScrolling) {
      triggerClose();
      return;
    }

    function triggerClose() {
      if (isClosing) {
        return;
      }

      isClosing = true;

      const paragraphText = editorView.state.selection.$from.parent.textBetween(
        0,
        editorView.state.selection.$from.parentOffset,
      );
      const atSymbolIndex = Math.max(
        paragraphText.lastIndexOf(MENTION_TEXT_PREFIX),
        paragraphText.lastIndexOf(FULL_WIDTH_AT_SYMBOL),
      );
      const rawQueryText = paragraphText.slice(atSymbolIndex + 1);
      const normalizedQuery = normalize(rawQueryText);

      closePopup(editorView, true, normalizedQuery);

      requestAnimationFrame(() => {
        isClosing = false;
      });
    }
  };

  const blurHandler = (): void => {
    if (!isClosing && !isMentionCurrentlyScrolling) {
      closePopup(editorView);
    }
  };

  document.addEventListener('click', outsideHandler, { capture: true });
  document.addEventListener('touchend', outsideHandler, { capture: true });
  editorView.dom.addEventListener('blur', blurHandler, true);

  return () => {
    document.removeEventListener('click', outsideHandler, true);
    document.removeEventListener('touchend', outsideHandler, true);
    editorView.dom.removeEventListener('blur', blurHandler, true);
  };
}

function openPopup(view: EditorView, state: PopupState): void {
  const currentIsMobile = isMobile();
  const currentIsAndroid = isAndroid();

  const popupBox = document.createElement('div');
  Object.assign(popupBox.style, {
    zIndex: '99999',
    position: currentIsMobile ? 'fixed' : 'absolute',
    background: '#fff',
    border: '1px solid #ccc',
    borderRadius: '4px',
    padding: '4px',
    maxHeight: currentIsMobile ? '200px' : '160px',
    overflowY: 'auto',
    width: currentIsMobile ? '90vw' : '360px',
    WebkitOverflowScrolling: 'touch',
    touchAction: 'pan-y',
    overscrollBehavior: 'contain',
    contain: 'layout style paint',
    isolation: 'isolate',
    willChange: 'scroll-position',
    transform: 'translateZ(0)',
    ...(currentIsMobile && {
      userSelect: 'none',
      WebkitUserSelect: 'none',
      WebkitTouchCallout: 'none',
      WebkitTapHighlightColor: 'transparent',
    }),
  });

  document.body.appendChild(popupBox);
  const removeScrollIsolation = setupScrollIsolation(popupBox);
  const removeAndroidScrollFix = currentIsAndroid ? setupAndroidScrollFix(popupBox) : () => {};

  const visualViewportObj = window.visualViewport;

  const updatePosition = (): void => {
    const isCurrentMobile = isMobile();
    const caretCoords = view.coordsAtPos(view.state.selection.$from.pos);

    if (isCurrentMobile) {
      const popupHeight = calculatePopupHeight(state.list.length, true);

      popupBox.style.left = '5vw';
      popupBox.style.width = '90vw';
      popupBox.style.position = 'fixed';

      const topPosition = Math.max(
        MINIMUM_TOP_SPACING,
        caretCoords.top - popupHeight - MINIMUM_TOP_SPACING,
      );
      popupBox.style.top = `${topPosition}px`;
      popupBox.style.bottom = 'auto';
    } else {
      const offsetX = visualViewportObj ? visualViewportObj.offsetLeft : window.scrollX;
      const offsetY = visualViewportObj ? visualViewportObj.offsetTop : window.scrollY;
      const popupHeight = calculatePopupHeight(state.list.length, false);
      const popupWidth = popupBox.offsetWidth;

      popupBox.style.position = 'absolute';
      popupBox.style.width = '360px';

      let left = caretCoords.left + offsetX - 30;
      const viewportWidth = visualViewportObj?.width ?? document.documentElement.clientWidth;
      const maxLeft = offsetX + viewportWidth - popupWidth - 8;

      if (left > maxLeft) {
        left = maxLeft;
      }
      if (left < 0) {
        left = 0;
      }

      popupBox.style.left = `${left}px`;
      popupBox.style.top = `${caretCoords.top + offsetY - popupHeight - MINIMUM_TOP_SPACING}px`;
    }
  };

  paint(view, popupBox, state.list, state.highlight);
  updatePosition();

  const removePointerListeners = addGlobalListeners(view, popupBox);
  const handleViewportChange = (): void => updatePosition();

  visualViewportObj?.addEventListener('resize', handleViewportChange);
  visualViewportObj?.addEventListener('scroll', handleViewportChange);
  window.addEventListener('scroll', handleViewportChange, true);
  window.addEventListener('resize', handleViewportChange);

  const removeAllListeners = (): void => {
    removePointerListeners();
    removeScrollIsolation();
    removeAndroidScrollFix();
    visualViewportObj?.removeEventListener('resize', handleViewportChange);
    visualViewportObj?.removeEventListener('scroll', handleViewportChange);
    window.removeEventListener('scroll', handleViewportChange, true);
    window.removeEventListener('resize', handleViewportChange);
  };

  const nextState: PopupState = {
    ...state,
    container: popupBox,
    isOpen: true,
    isHidden: false,
    removeGlobalListeners: removeAllListeners,
  };
  view.dispatch(view.state.tr.setMeta(MentionPluginKey, { next: nextState }));
}

function hidePopup(popupBox: HTMLDivElement): void {
  popupBox.style.display = 'none';
}

function showPopup(popupBox: HTMLDivElement): void {
  popupBox.style.display = 'block';
}

export function closePopup(
  view: EditorView,
  outsideClick: boolean = false,
  lastQuery?: string,
): void {
  const currentState = MentionPluginKey.getState(view.state);

  if (!currentState?.isOpen) {
    return;
  }

  currentState.removeGlobalListeners?.();
  currentState.container?.remove();
  const resetState: PopupState = {
    container: null,
    isOpen: false,
    isHidden: false,
    highlight: 0,
    list: [],
    anchor: null,
    wasClosedByOutsideClick: outsideClick,
    lastQuery: lastQuery ?? currentState.lastQuery,
  };
  view.dispatch(view.state.tr.setMeta(MentionPluginKey, { next: resetState }));
}

function findAtSymbol(view: EditorView): number {
  const { doc: documentNode } = view.state;
  let position = view.state.selection.from;
  while (position > 0) {
    const character = documentNode.textBetween(position - 1, position);

    if (character === MENTION_TEXT_PREFIX || character === FULL_WIDTH_AT_SYMBOL) {
      return position - 1;
    }

    if (character === '\n') {
      break;
    }

    position -= 1;
  }

  return position;
}

interface EditorViewWithUpdate extends EditorView {
  updateChannelMembers?: (members: ChannelMember[]) => void;
}

// メンションテキストを生成する関数
function createMentionText(member: ChannelMember): string {
  return member.id === ALL_ID
    ? `${MENTION_TEXT_PREFIX}ALL`
    : `${MENTION_TEXT_PREFIX}${member.display_name}`;
}

// メンションの属性を生成する関数
function createMentionAttrs(member: ChannelMember): Record<string, unknown> {
  const baseUrl =
    member.user_type === ChannelMemberUserTypeEnum.TENANT_USER
      ? SHARED_URL.COMMUNITY_CHANNEL_MEMBERS_DETAIL_TENANT
      : SHARED_URL.COMMUNITY_CHANNEL_MEMBERS_DETAIL_COMPANY;

  return {
    id: member.id,
    label: member.display_name,
    url:
      member.id === ALL_ID
        ? ''
        : generatePath(baseUrl, { code: member.channel.code, id: member.id }),
  };
}

function insertMention(view: EditorView, member: ChannelMember): void {
  const startPosition = findAtSymbol(view);
  const endPosition = view.state.selection.to;

  const mentionText = createMentionText(member);
  const mentionMarkType = view.state.schema.marks.mention;

  const mentionMark = mentionMarkType ? mentionMarkType.create(createMentionAttrs(member)) : null;

  const mentionNode = view.state.schema.text(mentionText, mentionMark ? [mentionMark] : []);
  const spacePosition = startPosition + mentionNode.nodeSize;
  const nbspNode = view.state.schema.text(NON_BREAKING_SPACE);
  const finalPosition = spacePosition + 1;

  let transaction = view.state.tr
    .delete(startPosition, endPosition)
    .insert(startPosition, mentionNode)
    .insert(spacePosition, nbspNode);

  transaction = transaction
    .setSelection(TextSelection.create(transaction.doc, finalPosition))
    .setStoredMarks([]);

  view.dispatch(transaction);

  closePopup(view);
  view.focus();
}

function removeMentionMark(view: EditorView) {
  const mentionType = view.state.schema.marks.mention;
  if (!mentionType) {
    return;
  }
  const { doc, selection } = view.state;
  let start = selection.from;
  let end = selection.from;
  while (start > 0 && doc.rangeHasMark(start - 1, start, mentionType)) {
    start -= 1;
  }
  while (end < doc.nodeSize && doc.rangeHasMark(end, end + 1, mentionType)) {
    end += 1;
  }
  view.dispatch(view.state.tr.removeMark(start, end, mentionType).setStoredMarks([]));
}

export const createMentionPlugin = (initialMembers: ChannelMember[]): Plugin => {
  const membersRef = { current: initialMembers };
  const allEntry: ChannelMember = {
    id: ALL_ID,
    display_name: 'all',
    avatar_url: '',
  } as ChannelMember;

  return new Plugin<PopupState>({
    key: MentionPluginKey,
    state: {
      init: () => ({
        container: null,
        isOpen: false,
        isHidden: false,
        highlight: 0,
        list: [],
        anchor: null,
      }),
      apply: (transaction, previousState) =>
        transaction.getMeta(MentionPluginKey)?.next ?? previousState,
    },
    props: {
      handleKeyDown(view, event) {
        const mentionType = view.state.schema.marks.mention;
        const position = view.state.selection.from;
        const insideOrAdjacent =
          mentionType &&
          (view.state.selection.$from.marks().some((m) => m.type === mentionType) ||
            view.state.doc.rangeHasMark(position - 1, position, mentionType) ||
            view.state.doc.rangeHasMark(position, position + 1, mentionType));

        const deleteKey = event.key === 'Backspace' || event.key === 'Delete';
        if (insideOrAdjacent && deleteKey) {
          removeMentionMark(view);
          return false;
        }

        const popupState = MentionPluginKey.getState(view.state);
        if (!popupState?.isOpen || !popupState.container) {
          return false;
        }

        const visibleCount = Math.min(popupState.list.length, 10);
        if (event.key === 'ArrowDown' && visibleCount > 0) {
          event.preventDefault();
          const nextIndex = (popupState.highlight + 1) % visibleCount;
          const nextState = { ...popupState, highlight: nextIndex };
          view.dispatch(view.state.tr.setMeta(MentionPluginKey, { next: nextState }));
          paint(view, popupState.container, popupState.list, nextIndex);
          return true;
        }
        if (event.key === 'ArrowUp' && visibleCount > 0) {
          event.preventDefault();
          const nextIndex = (popupState.highlight - 1 + visibleCount) % visibleCount;
          const nextState = { ...popupState, highlight: nextIndex };
          view.dispatch(view.state.tr.setMeta(MentionPluginKey, { next: nextState }));
          paint(view, popupState.container, popupState.list, nextIndex);
          return true;
        }
        if (event.key === 'Enter' && visibleCount > 0) {
          event.preventDefault();
          if (popupState.isHidden) {
            closePopup(view);
            const tr = view.state.tr.deleteSelection().split(view.state.selection.from);
            view.dispatch(tr.scrollIntoView());
          } else {
            const chosenMember = popupState.list.slice(0, 10)[popupState.highlight];
            insertMention(view, chosenMember);
          }
          return true;
        }
        return false;
      },
      handleDOMEvents: {
        paste(view: EditorView) {
          const pasteStart: number = view.state.selection.from;
          setTimeout(() => {
            const mentionMarkType = view.state.schema.marks.mention;
            const previousCharacterIsMention: boolean =
              pasteStart > 0 &&
              mentionMarkType != null &&
              view.state.doc.rangeHasMark(pasteStart - 1, pasteStart, mentionMarkType);
            let transaction = view.state.tr;
            const pasteEnd: number = view.state.selection.to;

            if (previousCharacterIsMention) {
              transaction = transaction.insert(pasteStart, view.state.schema.text(' '));
            }

            const boldMarkType = view.state.schema.marks.bold ?? view.state.schema.marks.strong;
            // Only remove bold marks if previous character is a mention to prevent bold mentions
            if (boldMarkType != null && previousCharacterIsMention) {
              transaction = transaction.removeMark(pasteStart, pasteEnd, boldMarkType);
            }

            transaction = transaction.setStoredMarks([]);
            view.dispatch(transaction);
          }, 0);
          return false;
        },
      },
    },
    view(editorView) {
      (editorView as EditorViewWithUpdate).updateChannelMembers = (members) => {
        membersRef.current = members;
      };

      return {
        update() {
          const lastTransaction = editorView.state.tr;

          if (lastTransaction.getMeta(MentionPluginKey)) {
            return;
          }

          const popupState = MentionPluginKey.getState(editorView.state);
          const paragraphText = editorView.state.selection.$from.parent.textBetween(
            0,
            editorView.state.selection.$from.parentOffset,
          );
          const atSymbolIndex = Math.max(
            paragraphText.lastIndexOf(MENTION_TEXT_PREFIX),
            paragraphText.lastIndexOf(FULL_WIDTH_AT_SYMBOL),
          );

          if (atSymbolIndex === -1) {
            if (popupState?.isOpen) {
              closePopup(editorView);
            }
            return;
          }

          const isAtStart = atSymbolIndex === 0;
          const charBeforeAt = atSymbolIndex > 0 ? paragraphText[atSymbolIndex - 1] : '';
          const isSpaceBefore = charBeforeAt === ' ' || charBeforeAt === '\u3000';
          const isFullWidthBefore = charBeforeAt && charBeforeAt.charCodeAt(0) > 0x7f;

          if (!isAtStart && !isSpaceBefore && !isFullWidthBefore) {
            if (popupState?.isOpen) {
              closePopup(editorView);
            }
            return;
          }

          const paragraphStartPos = editorView.state.selection.$from.start();
          const atAbsolutePos = paragraphStartPos + atSymbolIndex;
          const mentionType = editorView.state.schema.marks.mention;
          if (
            mentionType &&
            editorView.state.doc.rangeHasMark(atAbsolutePos, atAbsolutePos + 1, mentionType)
          ) {
            if (popupState?.isOpen) {
              closePopup(editorView);
            }
            return;
          }

          const rawQueryText = paragraphText.slice(atSymbolIndex + 1);
          const normalizedQuery = normalize(rawQueryText);

          if (popupState?.wasClosedByOutsideClick) {
            if (
              (popupState.lastQuery !== normalizedQuery ||
                paragraphText.includes(MENTION_TEXT_PREFIX) ||
                paragraphText.includes(FULL_WIDTH_AT_SYMBOL)) &&
              editorView.hasFocus()
            ) {
              const anchor = editorView.coordsAtPos(editorView.state.selection.$from.pos);
              const filteredList = [allEntry, ...membersRef.current].filter((member) =>
                member.id === ALL_ID
                  ? 'all'.startsWith(normalizedQuery)
                  : normalize(member.display_name).includes(normalizedQuery),
              );
              if (filteredList.length === 0) {
                return;
              }
              const sortedMembers = [
                ...filteredList.filter((m) => m.id === ALL_ID),
                ...filteredList.filter((m) => m.id !== ALL_ID),
              ];
              openPopup(editorView, {
                container: null,
                isOpen: false,
                isHidden: false,
                highlight: 0,
                list: sortedMembers,
                anchor,
                wasClosedByOutsideClick: false,
                lastQuery: normalizedQuery,
              });
            }
            return;
          }

          if (!popupState?.isOpen) {
            const anchor = editorView.coordsAtPos(editorView.state.selection.$from.pos);

            const filteredList = [allEntry, ...membersRef.current].filter((member) =>
              member.id === ALL_ID
                ? 'all'.startsWith(normalizedQuery)
                : normalize(member.display_name).includes(normalizedQuery),
            );
            if (filteredList.length > 0) {
              const sortedMembers = [
                ...filteredList.filter((m) => m.id === ALL_ID),
                ...filteredList.filter((m) => m.id !== ALL_ID),
              ];

              openPopup(editorView, {
                container: null,
                isOpen: false,
                isHidden: false,
                highlight: 0,
                list: sortedMembers,
                anchor,
                wasClosedByOutsideClick: false,
                lastQuery: normalizedQuery,
              });
            }
            return;
          }
          if (popupState?.isOpen && popupState.container) {
            const filteredList = [allEntry, ...membersRef.current].filter((member) =>
              member.id === ALL_ID
                ? 'all'.startsWith(normalizedQuery)
                : normalize(member.display_name).includes(normalizedQuery),
            );

            if (filteredList.length === 0) {
              if (!popupState.isHidden) {
                hidePopup(popupState.container);
                const hiddenState = { ...popupState, isHidden: true };
                editorView.dispatch(
                  editorView.state.tr.setMeta(MentionPluginKey, { next: hiddenState }),
                );
              }
              return;
            }

            const sortedMembers = [
              ...filteredList.filter((m) => m.id === ALL_ID),
              ...filteredList.filter((m) => m.id !== ALL_ID),
            ];

            const needRepaint =
              popupState.isHidden ||
              sortedMembers.length !== popupState.list.length ||
              sortedMembers.some((member, index) => member.id !== popupState.list[index]?.id);

            if (needRepaint) {
              if (popupState.isHidden) {
                showPopup(popupState.container);
              }

              const nextState = {
                ...popupState,
                list: sortedMembers,
                highlight: 0,
                isHidden: false,
              };

              editorView.dispatch(
                editorView.state.tr.setMeta(MentionPluginKey, { next: nextState }),
              );
              paint(editorView, popupState.container, sortedMembers, 0);

              const caretCoords = editorView.coordsAtPos(editorView.state.selection.$from.pos);
              const popupBox = popupState.container;
              if (popupBox && caretCoords) {
                const currentIsMobile = isMobile();
                if (currentIsMobile) {
                  const popupHeight = calculatePopupHeight(sortedMembers.length, true);

                  popupBox.style.left = '5vw';
                  popupBox.style.width = '90vw';
                  popupBox.style.position = 'fixed';

                  // Always show popup above the caret on mobile - calculate from top
                  const topPosition = Math.max(
                    MINIMUM_TOP_SPACING,
                    caretCoords.top - popupHeight - MINIMUM_TOP_SPACING,
                  );
                  popupBox.style.top = `${topPosition}px`;
                  popupBox.style.bottom = 'auto';
                } else {
                  const visualViewportObj = window.visualViewport;
                  const offsetY = visualViewportObj ? visualViewportObj.offsetTop : window.scrollY;
                  const popupHeight = calculatePopupHeight(sortedMembers.length, false);
                  popupBox.style.position = 'absolute';
                  popupBox.style.width = '360px';
                  popupBox.style.top = `${caretCoords.top + offsetY - popupHeight - MINIMUM_TOP_SPACING}px`;
                }
              }
            }
          }
        },
        destroy() {
          closePopup(editorView);
        },
      };
    },
  });
};
